import { Project, ProjectForResolver, UserForResolver } from "db/definitions";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { recordSentry } from "utils/recordSentry";
import _getProject from "../utils/_getProject";
import _getProjects from "../utils/_getProjects";
import createProjectAmplify from "../utils/createProjectAmplify";
import handleDeleteProject from "../utils/handleDeleteProject";
import { handleUpdateProject } from "../utils/handleUpdateProject";
import { deleteRecentProjects, getRecentProjects, RecentProject, updateRecentProjects } from "utils/localstorage/projects";
import handleGetProjectTemplates from "../utils/handleGetProjectTemplates";

const useProjects = (user: UserForResolver | undefined | null, online: boolean) => {
	const [projects, setProjects] = useState<ProjectForResolver[]>([]);
	const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);
	const [project, setProject] = useState<Project | null>(null);
	const [projectId, setProjectId] = useState("");
	const [rerender, setRerender] = useState(false);
	const [loadingProject, setLoadingProject] = useState(true);
	const { pathname } = useLocation();

	useEffect(() => {
		const regex = /project\/([^\/]+)/;
		const match = location.pathname.match(regex);
		if (match) {
			setProjectId(match[1]);
		}
	}, [pathname]);

	useEffect(() => {
		if (!user) return;
		setRecentProjects(
			getRecentProjects().filter((proj) => {
				// Recent projects before this won't have a group, so we need to check if the project has a group
				if (user.group === "FactorEarth" || !proj.group) return proj;
				return proj.group === user.group;
			}),
		);
	}, [user]);

	const getProjects = useCallback(async () => {
		if (!user) {
			setProjects([]);
			return;
		}
		try {
			const retrieved = await _getProjects(online, user);
			const copy = [...retrieved];
			if (online) {
				const offlineProjects = await _getProjects(false, user);
				for (let i = 0; i < offlineProjects.length; i++) {
					const foundProject = copy.find((proj) => proj.id === offlineProjects[i].id);
					if (foundProject) {
						// If the project is online and offline, marks it as such so if it's deleted while online, we still see it in
						// sync menu
						// @ts-ignore
						foundProject.offlineAndOnline = true;
					} else {
						// Prevents it from being displayed in the project list, but will show in the sync menu
						// @ts-ignore
						offlineProjects[i].offline = true;
						copy.push(offlineProjects[i]);
					}
				}
			}
			setProjects(copy);
			setLoadingProject(false);
		} catch (error) {
			recordSentry({ error, msg: "Error getting the projects:" });
		}
	}, [online, user]);

	useEffect(() => {
		if (user) getProjects();
	}, [getProjects, user]);

	/**
	 * Given a project id, retrieves it and sets it to the selected project
	 * @param {string} id The id of the project we want to get
	 * @returns Absolutely nothing
	 */
	async function getProject(id: string): Promise<Project | null> {
		if (!id) {
			setProject(null);
			return null;
		}
		const retrieved = await _getProject(id, online);
		if (retrieved?.sync) {
			setProject(retrieved);
		} else {
			setProject(null);
		}
		setLoadingProject(false);
		return retrieved;
	}

	useEffect(() => {
		// THIS FUNCTION IS THE SAME AS getProject above, but because of re-rendering bs, I copied and pasted it
		async function getProjectAgain(id: string) {
			if (!id) {
				setProject(null);
				return;
			}
			setLoadingProject(true);
			const retrieved = await _getProject(id, online);
			if (retrieved) {
				setProject(retrieved);
				const updatedRecentProjects = updateRecentProjects(retrieved);
				setRecentProjects(updatedRecentProjects);
			} else {
				setProject(null);
			}
			setLoadingProject(false);
			if (retrieved && online) {
				handleGetProjectTemplates(retrieved)
					.then((syncFormTemplates) => {
						if (syncFormTemplates) {
							setProject((prev) => (prev?.id === retrieved.id ? { ...prev, syncFormTemplates } : prev));
						}
					})
					.catch(() => {});
			}
		}
		getProjectAgain(projectId);
	}, [online, projectId, rerender]);

	function updateProjectsSyncMenu(proj: Project) {
		const projectsCopy = [...projects];
		const index = projectsCopy.findIndex((p) => p.id === proj.id);
		if (index !== -1) {
			projectsCopy.splice(index, 1, proj);
		} else {
			projectsCopy.push(proj);
		}
		setProjects(projectsCopy);
		setProject(proj);
	}

	async function createProject(data: Pick<ProjectForResolver, "name" | "description">) {
		if (!user) return;
		const created = await createProjectAmplify(data, online, user);
		if (created) {
			const projectsCopy = [...projects];
			projectsCopy.push(created);
			setProjects(projectsCopy);
			setProject(created);
			setProjectId(created.id);
			const updatedRecentProjects = updateRecentProjects(created);
			setRecentProjects(updatedRecentProjects);
			return created;
		}
	}

	async function updateProject(updatedProject: Project) {
		await handleUpdateProject(updatedProject, online);
		const updatedProjects = projects.map((proj) => {
			if (proj.id === updatedProject.id) return updatedProject;
			return proj;
		});
		setProjects(updatedProjects);
		setProject(updatedProject);
		setProjectId(updatedProject.id);
		setRerender(!rerender);
	}

	async function deleteProject(project: Project) {
		if (!user) return;

		await handleDeleteProject(project, user, online);
		const newProjects = [];
		for (const proj of projects) {
			if (proj.id === project.id) {
				// If this condition isn't hit, they're removed. Otherwise, kept, but labeled as offline so you can still see it in the
				// sync menu
				// @ts-ignore
				if (online && proj.offlineAndOnline) {
					// @ts-ignore
					proj.offline = true;
					// @ts-ignore
					delete proj.offlineAndOnline;
					newProjects.push(proj);
				}
			} else {
				newProjects.push(proj);
			}
		}
		setProject(null);
		setProjectId("");
		setProjects(newProjects);
		const newRecentProjects = deleteRecentProjects(project);
		setRecentProjects(newRecentProjects);
	}

	return {
		createProject,
		deleteProject,
		getProject,
		getProjects,
		loadingProject,
		project,
		projectId,
		projects,
		recentProjects,
		setProjectId,
		updateProject,
		updateProjectsSyncMenu,
	};
};

export default useProjects;
